import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { ALL_SECTIONS } from '../data';
import StatCard from '../components/StatCard';

export default function UsersRolesPage() {
  const { roles, users, openModal } = useApp();
  const [selectedRole, setSelectedRole] = useState(null);

  const activeUsers  = users.filter(u => u.status === 'active').length;
  const pendingUsers = users.filter(u => u.status === 'pending').length;

  const role = selectedRole ? roles.find(r => r.id === selectedRole) : null;

  return (
    <div>
      {/* Stats */}
      <div className="sg sg-4" style={{ marginBottom: 16 }}>
        <StatCard label="Team Members" value={users.length} desc="Total staff" />
        <StatCard label="Active" value={activeUsers} desc="Currently active" variant="gr" />
        <StatCard label="Pending" value={pendingUsers} desc="Invitation sent" variant="am" />
        <StatCard label="Roles" value={roles.length} desc="Permission sets" variant="bl" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 16 }}>
        {/* Roles */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Roles</div>
            <button className="btn btn-primary btn-sm" onClick={() => openModal('role-editor')}>+ New Role</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {roles.map(r => {
              const memberCount = users.filter(u => u.roleId === r.id).length;
              const isSelected = selectedRole === r.id;
              return (
                <div
                  key={r.id}
                  onClick={() => setSelectedRole(isSelected ? null : r.id)}
                  style={{
                    padding: '10px 12px', borderRadius: 'var(--r)', cursor: 'pointer',
                    border: `1px solid ${isSelected ? 'var(--ac-b)' : 'var(--bd)'}`,
                    background: isSelected ? 'var(--ac-l)' : 'var(--sur)',
                    boxShadow: 'var(--sh)', transition: 'all .12s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 18 }}>{r.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{r.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{r.desc}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: 'var(--tx3)' }}>{memberCount} member{memberCount !== 1 ? 's' : ''}</span>
                      <button className="btn btn-xs btn-ghost" onClick={e => { e.stopPropagation(); openModal('role-editor', r.id); }}>Edit</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Users table */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Team Members</div>
            <button className="btn btn-primary btn-sm" onClick={() => openModal('invite-user')}>+ Invite User</button>
          </div>
          <div className="tbl">
            <div className="tbl-hd" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr .6fr' }}>
              <div className="th">Name</div>
              <div className="th">Email</div>
              <div className="th">Role</div>
              <div className="th">Last Active</div>
              <div className="th">Status</div>
              <div className="th"></div>
            </div>
            {users.map(u => {
              const r = roles.find(x => x.id === u.roleId);
              return (
                <div key={u.id} className="tr" style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr .6fr' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: u.color || 'var(--ac)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                      {u.initials}
                    </div>
                    <div>
                      <div className="an">{u.fname} {u.lname}</div>
                      <div className="ap">{u.title || ''}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{u.email}</div>
                  <div style={{ fontSize: 11 }}>{r?.name || u.roleId}</div>
                  <div className="dv">{u.lastActive || '—'}</div>
                  <div>
                    <span className={`sp ${u.status === 'active' ? 's-ac' : u.status === 'pending' ? 's-bk' : 's-av'}`}>
                      {u.status?.charAt(0).toUpperCase() + u.status?.slice(1)}
                    </span>
                  </div>
                  <div className="abtns">
                    <button className="ab ab-st" onClick={() => openModal('invite-user', u.id)}>Edit</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Permission matrix */}
      {role && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>Permissions — {role.name}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {ALL_SECTIONS.map(sec => {
              const perm = role.perms?.[sec.key] || { view: false, edit: false };
              return (
                <div key={sec.key} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                  border: '1px solid var(--bd)', borderRadius: 'var(--r)', fontSize: 11,
                  background: perm.view ? 'var(--sur)' : 'var(--sur2)',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500 }}>{sec.label}</div>
                    <div style={{ fontSize: 9, color: 'var(--tx3)' }}>{sec.desc}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                      background: perm.view ? 'var(--gr-l)' : 'var(--bd)',
                      color: perm.view ? 'var(--gr)' : 'var(--tx3)',
                    }}>View</span>
                    <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
                      background: perm.edit ? 'var(--bl-l)' : 'var(--bd)',
                      color: perm.edit ? 'var(--bl)' : 'var(--tx3)',
                    }}>Edit</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
